import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { Notification, WorkArea } from '@xsolla/ui-core';

import { LoginProjectConfiguration, setSocialConnections } from '../../api';
import { LoginMethodView } from '../../components';
import {
  Breadcrumbs,
  FormFooter,
  PageHeader,
  Preloader,
  RadioGroup,
  ToDashboardError
} from '../../components/ui';
import { Notice } from '../../components/ui/icons';
import { CLASSIC_LOGIN, PASSWORDLESS_LOGIN, SOCIAL_LOGIN } from '../../constants';
import { useSocialConnections } from '../../contexts/settings';
import { CurrentProjectType } from '../../contexts/settings/types';
import { usePaths } from '../../routes/routesHelper';
import { RouteParams } from '../../routes/types';
import {
  ChangeMethodBlock,
  ChangeMethodSubtitle,
  ChangeMethodTitle,
  FooterWrapper,
  MainContent,
  MainContentWrapper
} from './style';

type Props = RouteComponentProps & {
  configurations: LoginProjectConfiguration | null;
  updateConfigurations: (
    configurations: LoginProjectConfiguration
  ) => Promise<LoginProjectConfiguration>;
  currentProjectType: CurrentProjectType;
};

enum LoginMethods {
  classic = 'classic',
  passwordless = 'passwordless',
  social = 'social'
}

const ChangeMethodPagePure = ({
  configurations,
  updateConfigurations,
  currentProjectType,
  match,
  history
}: Props): JSX.Element => {
  const intl = useIntl();
  const { projectId } = match.params as RouteParams;
  const { dashboardPath, navigationPath } = usePaths();

  const [fetching, setFetching] = useState<boolean>(false);
  const [connectionsFetching, setConnectionsFetching] = useState<boolean>(true);
  const [updateMode, setUpdateMode] = useState<boolean>(false);
  const [activeMethod, setActiveMethod] = useState<LoginMethods>(LoginMethods.classic);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const {
    currentSocials,
    setCurrentSocials,
    error: socialsError
  } = useSocialConnections(projectId, updateMode);

  useEffect(() => {
    if (updateMode) {
      return;
    }

    if (configurations) {
      if (currentProjectType === PASSWORDLESS_LOGIN) {
        setConnectionsFetching(false);
        setActiveMethod(LoginMethods.passwordless);
        return;
      }
      if (currentProjectType === CLASSIC_LOGIN) {
        setConnectionsFetching(false);
        setActiveMethod(LoginMethods.classic);
        return;
      }
      if (
        currentProjectType === SOCIAL_LOGIN &&
        currentSocials &&
        currentSocials.find((network) => network.connected || network.is_custom)
      ) {
        setConnectionsFetching(false);
        setActiveMethod(LoginMethods.social);
      }
    }
  }, [configurations, currentSocials, updateMode]);

  const backToNavigation = useCallback(() => history.push(navigationPath(projectId)), [projectId]);

  const handleClick = useCallback(async () => {
    if (!configurations) {
      return;
    }

    try {
      setSubmitError(false);
      setFetching(true);
      setUpdateMode(true);
      if (activeMethod !== LoginMethods.social) {
        await updateConfigurations({
          ...configurations,
          authentication_allowed: activeMethod === LoginMethods.classic,
          is_username_email: activeMethod === LoginMethods.classic,
          registration_allowed: activeMethod === LoginMethods.classic,
          is_email_auth_allowed: activeMethod === LoginMethods.passwordless,
          is_phone_auth_allowed: activeMethod === LoginMethods.passwordless
        });
        setFetching(false);

        backToNavigation();
        return;
      }
      if (currentSocials && !currentSocials.find((network) => network.connected)) {
        await setSocialConnections({
          projectId,
          provider: currentSocials[0].provider,
          values: { client_id: null, client_secret: null }
        });

        const [connectedSocial, ...otherSocials] = currentSocials;
        setCurrentSocials([{ ...connectedSocial, connected: true }, ...otherSocials]);
      }
      await updateConfigurations({
        ...configurations,
        authentication_allowed: false,
        registration_allowed: false,
        is_phone_auth_allowed: false,
        is_email_auth_allowed: false
      });
      setFetching(false);
      backToNavigation();
    } catch (err) {
      setSubmitError(true);
      setFetching(false);
    }
  }, [activeMethod, currentSocials]);

  if (socialsError) {
    return <ToDashboardError error={socialsError} pageName="change-login-type_open" />;
  }

  return (
    <WorkArea>
      <PageHeader
        title={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <Breadcrumbs
            items={[
              {
                label: intl.messages['dashboard.label'],
                to: dashboardPath()
              },
              {
                label: configurations?.name,
                to: navigationPath(projectId)
              },
              {
                label: intl.messages['navigation.settings.authentication.label'],
                to: `#`
              }
            ]}
          />
        }
      />
      {connectionsFetching ? (
        <MainContentWrapper>
          <Preloader />
        </MainContentWrapper>
      ) : (
        <MainContentWrapper>
          <MainContent>
            <ChangeMethodTitle>{intl.messages['change-method.main-title']}</ChangeMethodTitle>
            <ChangeMethodSubtitle>
              {intl.messages['change-method.main-subtitle']}
            </ChangeMethodSubtitle>
            <ChangeMethodBlock>
              <Notification palette="brand" icon={Notice}>
                {intl.messages['change-method.main-notification']}
              </Notification>
            </ChangeMethodBlock>
            <ChangeMethodBlock>
              <RadioGroup
                name="auth-method"
                defaultValue={activeMethod}
                onChange={(e) => setActiveMethod(e.target.value as LoginMethods)}
                options={Object.keys(LoginMethods).map((methodName) => ({
                  label: intl.messages[`change-method.methods.${methodName}.title`],
                  description: intl.messages[`change-method.methods.${methodName}.subtitle`],
                  radioValue: methodName
                }))}
              />
            </ChangeMethodBlock>
          </MainContent>
          <LoginMethodView methodName={activeMethod} />

          <FooterWrapper>
            <FormFooter
              onSubmit={handleClick}
              onCancel={backToNavigation}
              submitText={intl.formatMessage({ id: 'change-method.button.save' })}
              fetching={fetching}
              error={submitError}
            />
          </FooterWrapper>
        </MainContentWrapper>
      )}
    </WorkArea>
  );
};

export const ChangeMethodPage = withRouter(ChangeMethodPagePure);
